/**
 * Type definitions for EnhancedGenericCreateForm
 * Re-exports from UniversalCRMTable/types for convenience
 */

import { z } from "zod";
import type { SimpleTableConfig } from "../UniversalCRMTable/types/index";

export type {
  BaseEntity,
  SimpleTableConfig,
  EnhancedGenericFormProps,
} from "../UniversalCRMTable/types/index";

/**
 * Type for form data derived from a SimpleTableConfig
 * Extracts field values from the config's fields
 */
export type FormDataFromConfig<Config extends SimpleTableConfig> = {
  [K in keyof Config["fields"]]: Config["fields"][K] extends { type: infer T }
    ? T extends "string"
      ? string
      : T extends "number"
        ? number
        : T extends "boolean"
          ? boolean
          : T extends "date"
            ? string | Date
            : T extends "select"
              ? string
              : T extends "multiselect"
                ? string[]
                : unknown
    : unknown;
};

/**
 * Type guard to check if an error is a ZodError
 */
export function isZodError(error: unknown): error is z.ZodError {
  return (
    error instanceof z.ZodError ||
    (typeof error === "object" &&
      error !== null &&
      "issues" in error &&
      Array.isArray((error as any).issues))
  );
}

/** Strip stack traces, file paths, and code from a message so it's safe to show to users */
function sanitizeForUser(msg: string): string {
  if (!msg || typeof msg !== "string") return "Something went wrong. Please try again.";
  // Remove lines that look like stack frames (at ..., file path, line numbers)
  const lines = msg.split("\n").filter((line) => {
    const t = line.trim();
    return (
      t.length > 0 &&
      !/^\s*at\s+/.test(line) &&
      !/\(.*:\d+:\d+\)/.test(t) &&
      !/^[A-Z]:\\/.test(t) &&
      !/^\//.test(t)
    );
  });
  const out = lines[0]?.trim() || msg.trim();
  return out.length > 0 ? out : "Something went wrong. Please try again.";
}

/** Turn a Zod issue into a short user-safe reason (no code or internal wording) */
function friendlyZodMessage(issue: z.ZodIssue): string {
  const msg = issue.message || "";
  if (issue.code === "invalid_type") {
    if (msg.includes("number") && msg.includes("string")) return "Must be a number.";
    if (msg.includes("string")) return "Must be text.";
  }
  if (issue.code === "too_small" && msg.length > 0) return msg;
  if (issue.code === "too_big" && msg.length > 0) return msg;
  return msg || "Invalid value.";
}

/**
 * Format ZodError as user-safe "Field: reason" lines. Never exposes code or paths.
 */
export function formatZodErrorForUser(error: z.ZodError): string {
  return error.issues
    .map((issue) => {
      const path = issue.path?.filter(Boolean).join(" ");
      const label = path ? path.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) : "Field";
      const reason = friendlyZodMessage(issue);
      return `${label}: ${reason}`;
    })
    .join(". ");
}

/**
 * Extract a user-safe message from an unknown error. Never exposes stack traces or code.
 */
export function getErrorMessage(error: unknown): string {
  if (isZodError(error)) {
    return formatZodErrorForUser(error);
  }
  if (error instanceof Error) {
    return sanitizeForUser(error.message);
  }
  if (typeof error === "string") {
    return sanitizeForUser(error);
  }
  return "Something went wrong. Please try again.";
}
